import { useMemo } from 'react';
import {
  getPlatformSpecs,
  getCharacterStatus,
  splitIntoThreads,
} from '../constants/platformSpecs';

/**
 * Strip HTML tags from content
 * @param {string} html - HTML content
 * @returns {string} Plain text
 */
const stripHtml = (html) => {
  if (typeof window === 'undefined') {
    // Server-side fallback
    return html?.replace(/<[^>]*>/g, '') || '';
  }
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html || '';
  return tmp.textContent || tmp.innerText || '';
};

/**
 * Custom hook for platform-specific content validation
 *
 * @param {string} content - The content to validate (can be HTML)
 * @param {string} platformType - The platform type ('linkedin', 'facebook', 'x', 'instagram')
 * @param {string[]} hashtags - Array of hashtags (optional)
 * @returns {Object} Validation result
 */
const usePlatformValidation = (content, platformType, hashtags = []) => {
  return useMemo(() => {
    const specs = getPlatformSpecs(platformType);
    const plainContent = stripHtml(content);
    const charCount = plainContent.length;

    // If no platform selected, return basic validation
    if (!specs) {
      return {
        isValid: true,
        charCount,
        limit: null,
        remaining: null,
        status: 'ok',
        message: null,
        platform: null,
        threads: null,
        hashtagStatus: null,
      };
    }

    const { characterLimit, warningThreshold, hashtagLimit, supportsThreads } = specs;
    const status = getCharacterStatus(charCount, platformType);
    const remaining = characterLimit - charCount;
    const isOverLimit = charCount > characterLimit;

    // Thread calculation for X/Twitter
    let threads = null;
    if (supportsThreads && isOverLimit) {
      threads = splitIntoThreads(plainContent, characterLimit);
    }

    // Hashtag validation
    let hashtagStatus = null;
    if (hashtagLimit !== null) {
      const hashtagCount = hashtags.length;
      if (hashtagCount > hashtagLimit) {
        hashtagStatus = {
          isValid: false,
          count: hashtagCount,
          limit: hashtagLimit,
          status: 'error',
          message: `Too many hashtags (${hashtagCount}/${hashtagLimit})`,
        };
      } else if (hashtagCount >= hashtagLimit * 0.9) {
        hashtagStatus = {
          isValid: true,
          count: hashtagCount,
          limit: hashtagLimit,
          status: 'warning',
          message: `${hashtagLimit - hashtagCount} hashtags remaining`,
        };
      } else {
        hashtagStatus = {
          isValid: true,
          count: hashtagCount,
          limit: hashtagLimit,
          status: 'ok',
          message: null,
        };
      }
    }

    // Generate status message (skip for threads - shown in CharacterCounter)
    let message = null;
    if (status === 'error' && !supportsThreads) {
      message = `Content exceeds the ${characterLimit} character limit by ${Math.abs(remaining)} characters.`;
    } else if (status === 'warning') {
      message = `${remaining} characters remaining.`;
    }

    // Overall validity (X allows over-limit content via threads)
    const isValid = supportsThreads ? true : !isOverLimit;

    return {
      isValid,
      charCount,
      limit: characterLimit,
      remaining,
      percentage: Math.min((charCount / characterLimit) * 100, 100),
      status,
      message,
      platform: specs.name,
      threads,
      threadCount: threads?.length || null,
      hashtagStatus,
      specs,
    };
  }, [content, platformType, hashtags]);
};

export default usePlatformValidation;
